import { apply, celebrate } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { sayEnglish } from '../audio/speech';
import { sfx } from '../audio/sfx';
import { gradeOf, makeWorksheet, type Grade, type Question, type WorksheetKind } from '../content/lifeWorksheet';
import { life } from '../content/money';
import { logSpell } from '../content/passport';
import { practice, logJournal } from '../content/journal';
import { h, type Child } from './dom';
import { closePanel, openPanel, refreshPanel } from './panel';
import { PLACE_STAMPS } from './passport';

export type { WorksheetKind };

/** A graded 5-question worksheet (homework, quiz or exam); `onDone` runs when it's handed in. */
const ws = {
  kind: 'homework' as WorksheetKind,
  qs: [] as Question[],
  i: 0,
  right: [] as boolean[],
  picked: '',
  tiles: [] as { ch: string; used: boolean }[],
  typed: [] as number[],
  answered: false,
  onDone: null as null | ((grade: Grade, score: number) => void),
};

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function setup(): void {
  const q = ws.qs[ws.i];
  ws.picked = '';
  ws.typed = [];
  ws.answered = false;
  ws.tiles = [];
  if (q?.type === 'spell') {
    const decoys = 'aeiourstnl'.split('').filter((c) => !q.word.includes(c)).slice(0, 2);
    ws.tiles = shuffle([...q.word.split(''), ...decoys]).map((ch) => ({ ch, used: false }));
    window.setTimeout(() => sayEnglish(q.word, 0.75), 300);
  }
}

export function openWorksheet(kind: WorksheetKind, onDone: (grade: Grade, score: number) => void): void {
  ws.kind = kind;
  ws.qs = makeWorksheet(kind, S().grade);
  ws.i = 0;
  ws.right = [];
  ws.onDone = onDone;
  setup();
  openPanel('worksheet', render);
}

function answer(ok: boolean): void {
  ws.answered = true;
  ws.right[ws.i] = ok;
  sfx(ok ? 'ok' : 'bad');
  const q = ws.qs[ws.i];
  if (ok && q.type === 'spell') logSpell(q.word);
  if (ok && q.type === 'reading') practice('reading');
  if ('say' in q && q.say && (q.type === 'cloze' || ok)) sayEnglish(q.say, 0.85);
  refreshPanel('worksheet');
}

function finish(): void {
  const score = ws.right.filter(Boolean).length;
  const g = gradeOf(score, ws.qs.length);
  const l = life();
  l.works.push({ kind: ws.kind, grade: g, score, week: S().week });
  if (l.works.length > 30) l.works.splice(0, l.works.length - 30);
  if (g === 'A') logJournal('📝', 'life.journal.worksheet', g);
  const cb = ws.onDone;
  ws.onDone = null;
  closePanel('worksheet');
  cb?.(g, score);
}

function render() {
  const t = m().life.ws;
  const n = ws.qs.length;
  const title = `${t.kinds[ws.kind]} · ${t.gradeLabel(S().grade)}`;
  if (ws.i >= n) {
    const score = ws.right.filter(Boolean).length;
    const g = gradeOf(score, n);
    return {
      title,
      className: 'life-ws',
      closable: false,
      body: [
        h('div', { class: `life-ws-stamp g${g}` }, g),
        h('p', { class: 'life-ws-score' }, t.result(score, n)),
        h('p', { class: 'hint big' }, t.comments[g]),
        h('div', { class: 'life-ws-dots' }, ws.right.map((ok) => h('span', { class: ok ? 'ok' : 'bad' }, ok ? '✓' : '✗'))),
      ],
      footer: [h('button', { class: 'btn gold', onclick: () => (sfx('click'), finish()) }, t.done)],
    };
  }
  const q = ws.qs[ws.i];
  const dots = h(
    'div',
    { class: 'life-ws-dots' },
    ws.qs.map((_, k) => h('span', { class: k < ws.i || (k === ws.i && ws.answered) ? (ws.right[k] ? 'ok' : 'bad') : k === ws.i ? 'cur' : '' }, String(k + 1))),
  );
  const head = h('div', { class: 'life-ws-head' }, h('b', null, t.types[q.type]), h('small', null, t.progress(ws.i + 1, n)));
  const fb = ws.answered
    ? h('p', { class: `hint big ${ws.right[ws.i] ? 'ok' : 'bad'}` }, ws.right[ws.i] ? t.right : t.wrong(q.answer))
    : null;
  const nextBtn = ws.answered
    ? h('button', { class: 'btn gold wide', onclick: () => (sfx('click'), ws.i++, setup(), refreshPanel('worksheet')) }, ws.i + 1 >= n ? t.finish : t.next)
    : null;
  return { title, className: 'life-ws', body: [dots, h('div', { class: 'life-ws-paper' }, head, question(q)), fb, nextBtn] };
}

function question(q: Question): Child[] {
  const t = m().life.ws;
  if (q.type === 'spell') {
    const typed = ws.typed.map((k) => ws.tiles[k].ch).join('');
    const state = ws.answered ? (ws.right[ws.i] ? 'ok' : 'bad') : '';
    return [
      h('p', { class: 'hint' }, t.spellHint),
      h('div', { class: 'life-ws-q' }, h('b', null, q.meaning), h('button', { class: 'btn small blue', onclick: () => sayEnglish(q.word, 0.7) }, t.hear)),
      h('div', { class: `spell-slots ${state}` }, q.word.split('').map((_, k) => h('span', { class: 'slot' }, typed[k] ?? ''))),
      h(
        'div',
        { class: 'spell-tiles' },
        ws.tiles.map((tile, k) =>
          h(
            'button',
            {
              class: 'tile',
              disabled: tile.used || ws.answered,
              onclick: () => {
                tile.used = true;
                ws.typed.push(k);
                sfx('click');
                const full = ws.typed.map((x) => ws.tiles[x].ch).join('');
                if (full.length >= q.word.length) answer(full === q.word);
                else refreshPanel('worksheet');
              },
            },
            tile.ch,
          ),
        ),
      ),
      ws.answered
        ? h('span')
        : h(
            'button',
            {
              class: 'btn small ghost',
              onclick: () => {
                const k = ws.typed.pop();
                if (k !== undefined) ws.tiles[k].used = false;
                refreshPanel('worksheet');
              },
            },
            t.undo,
          ),
    ];
  }
  const hint = { cloze: t.clozeHint, math: t.mathHint, reading: t.readingHint, logic: t.logicHint }[q.type];
  return [
    h('p', { class: 'hint' }, hint),
    q.passage ? h('div', { class: 'life-ws-passage' }, h('button', { class: 'life-ws-say', onclick: () => sayEnglish(q.passage!, 0.85) }, '🔊'), q.passage) : null,
    h('div', { class: `life-ws-prompt ${q.type}` }, q.prompt),
    h(
      'div',
      { class: `life-ws-opts${q.options.every((o) => o.length <= 12) ? ' two' : ''}` },
      q.options.map((o) =>
        h(
          'button',
          {
            class: `btn life-ws-opt${ws.answered && o === q.answer ? ' right' : ''}${ws.answered && o === ws.picked && o !== q.answer ? ' wrong' : ''}`,
            disabled: ws.answered,
            onclick: () => {
              ws.picked = o;
              if (q.type === 'cloze' || q.type === 'reading') sayEnglish(o, 0.9);
              answer(o === q.answer);
            },
          },
          o,
        ),
      ),
    ),
  ];
}

// --- Exhibition (成果展) and portfolio -------------------------------------------------------------

const RANK: Record<Grade, number> = { A: 3, B: 2, C: 1 };

/** Best worksheet results, best first (kind, grade, score). */
export function bestWorks(n = 5): { kind: string; grade: Grade; score: number; week: number }[] {
  return [...life().works].sort((a, b) => RANK[b.grade] - RANK[a.grade] || b.score - a.score || b.week - a.week).slice(0, n);
}

/** Every 4th week the school exhibits your work; `exhibition` gives a reward, the portfolio is a free look. */
export function openExhibition(exhibition = false): void {
  openPanel('lifeExhibit', () => {
    const t = m().life.exhibit;
    const s = S();
    const works = bestWorks();
    const drawings = s.posts.filter((p) => p.img).slice(-6).reverse();
    const stamps = PLACE_STAMPS.filter((p) => s.english.stamps[p.id] !== undefined);
    const nextW = Math.ceil((s.week + 1) / 4) * 4;
    return {
      title: exhibition ? t.title : t.titlePortfolio,
      className: 'life-exhibit',
      body: [
        exhibition ? h('p', { class: 'hint' }, t.today.replace(/\*\*/g, '')) : h('p', { class: 'hint' }, t.next(s.week % 4 === 0 ? s.week : nextW)),
        h('h4', null, `📝 ${t.works}`),
        works.length
          ? h(
              'div',
              { class: 'life-ex-works' },
              works.map((w) =>
                h(
                  'div',
                  { class: 'life-ex-work' },
                  h('span', { class: `life-ws-stamp mini g${w.grade}` }, w.grade),
                  h('small', null, m().life.ws.kinds[w.kind as WorksheetKind] ?? w.kind),
                  h('small', { class: 'muted' }, `${w.score}/5 · W${w.week}`),
                ),
              ),
            )
          : h('p', { class: 'hint' }, t.noWorks),
        h('h4', null, `🎨 ${t.drawings}`),
        drawings.length
          ? h('div', { class: 'life-ex-gallery' }, drawings.map((p) => h('figure', null, h('img', { src: p.img, alt: '' }))))
          : h('p', { class: 'hint' }, t.noDrawings),
        h('h4', null, `🛂 ${t.stamps}`),
        stamps.length
          ? h(
              'div',
              { class: 'life-ex-stamps' },
              stamps.map((p) => h('span', { class: 'life-ex-stamp', style: `border-color:${p.color};color:${p.color}` }, p.icon, h('small', null, m().passport.stampNames[p.id]))),
            )
          : h('p', { class: 'hint' }, t.noStamps),
      ],
    };
  });
}

/** Exhibition day reward: points for each kind of work shown. Returns the stars given. */
export function exhibitReward(): number {
  const s = S();
  const n = Math.min(5, bestWorks().length) + Math.min(6, s.posts.filter((p) => p.img).length) + Math.min(6, Object.keys(s.english.stamps).length);
  const stars = 5 + n * 2;
  celebrate(true);
  apply({ stars, mood: 10, love: { mom: 5 } });
  if (s.posts.some((p) => p.img)) practice('art');
  return stars;
}
